import { computed, ref, type Ref } from 'vue'
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  keepPreviousData,
} from '@tanstack/vue-query'
import { portfolioSummaryService } from '../services/api'
import {
  mergeHistoricalWithCurrent,
  sortSummariesByDateAsc,
  flattenPages,
} from '../services/summary-aggregator'
import { useAuthState } from './use-auth-state'
import { useSummaryCalendarQuery } from './use-summary-calendar-query'
import { DEFAULT_CHART_RANGE } from './use-time-range'
import { BENCHMARKS, type ChartBenchmark, type ChartSummary } from './use-portfolio-chart'
import { type BenchmarkIndex, TimeRange } from '../models/generated/domain-models'

const INTRADAY_RANGES: TimeRange[] = [
  TimeRange.ONE_DAY,
  TimeRange.TWO_DAYS,
  TimeRange.THREE_DAYS,
  TimeRange.FOUR_DAYS,
  TimeRange.FIVE_DAYS,
  TimeRange.SIX_DAYS,
  TimeRange.ONE_WEEK,
]

const MIN_INTRADAY_POINTS = 2

export function usePortfolioSummaryQuery(
  selectedPlatforms?: Ref<string[]>,
  selectedRange?: Ref<TimeRange>,
  isMonthly: Ref<boolean> = ref(false)
) {
  const queryClient = useQueryClient()
  const recalculationMessage = ref('')
  const pageSize = 30
  const { isAuthenticated } = useAuthState()

  const platformsKey = computed(() => selectedPlatforms?.value ?? [])
  const activePlatforms = computed(() =>
    platformsKey.value.length > 0 ? platformsKey.value : undefined
  )
  const rangeKey = computed(() => selectedRange?.value ?? DEFAULT_CHART_RANGE)

  const {
    data: historicalData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingHistorical,
    error: historicalError,
  } = useInfiniteQuery({
    queryKey: ['portfolio-summary', 'historical', platformsKey],
    queryFn: ({ pageParam = 0 }) =>
      portfolioSummaryService.getHistorical(pageParam, pageSize, activePlatforms.value),
    getNextPageParam: (lastPage, allPages) => {
      if (allPages.length < lastPage.totalPages) {
        return allPages.length
      }
      return undefined
    },
    initialPageParam: 0,
    enabled: isAuthenticated,
  })

  const { data: currentSummary, isLoading: isLoadingCurrent } = useQuery({
    queryKey: ['portfolio-summary', 'current', platformsKey],
    queryFn: () => portfolioSummaryService.getCurrent(activePlatforms.value),
    enabled: isAuthenticated,
  })

  const calendar = useSummaryCalendarQuery({
    platformsKey,
    activePlatforms,
    isAuthenticated,
    currentSummary,
    isMonthly,
  })

  const {
    data: seriesData,
    isFetching: isFetchingSeries,
    error: seriesError,
  } = useQuery({
    queryKey: ['portfolio-summary', 'series', platformsKey, rangeKey],
    queryFn: () => portfolioSummaryService.getSeries(rangeKey.value, activePlatforms.value),
    placeholderData: keepPreviousData,
    enabled: isAuthenticated,
  })

  const isIntradayRange = computed(() => INTRADAY_RANGES.includes(rangeKey.value))

  const {
    data: intradayData,
    error: intradayError,
    isPlaceholderData: isIntradayStale,
  } = useQuery({
    queryKey: ['portfolio-summary', 'intraday', platformsKey, rangeKey],
    queryFn: () => portfolioSummaryService.getIntraday(rangeKey.value, activePlatforms.value),
    placeholderData: keepPreviousData,
    enabled: computed(() => isAuthenticated.value && isIntradayRange.value),
  })

  const benchmarkQuery = (index: BenchmarkIndex) =>
    useQuery({
      queryKey: ['portfolio-summary', 'benchmark', rangeKey, index],
      queryFn: () => portfolioSummaryService.getBenchmark(rangeKey.value, index),
      placeholderData: keepPreviousData,
      enabled: isAuthenticated,
    })

  const benchmarkQueries = BENCHMARKS.map(benchmark => ({
    benchmark,
    query: benchmarkQuery(benchmark.index),
  }))

  const { data: rangeChange, error: rangeChangeError } = useQuery({
    queryKey: ['portfolio-summary', 'range-change', platformsKey, rangeKey],
    queryFn: async () => ({
      range: rangeKey.value,
      platforms: platformsKey.value.join(','),
      ...(await portfolioSummaryService.getRangeChange(rangeKey.value, activePlatforms.value)),
    }),
    placeholderData: keepPreviousData,
    enabled: isAuthenticated,
  })

  const recalculateMutation = useMutation({
    mutationFn: portfolioSummaryService.recalculate,
    onSuccess: response => {
      recalculationMessage.value = response.message
      queryClient.invalidateQueries({ queryKey: ['portfolio-summary'] })
      queryClient.invalidateQueries({ queryKey: ['instruments'] })
    },
    onError: () => {
      recalculationMessage.value = 'Failed to recalculate summaries. Please try again later.'
    },
  })

  const summaries = computed(() => {
    const historicalSummaries = flattenPages(historicalData.value?.pages)
    return mergeHistoricalWithCurrent(historicalSummaries, currentSummary.value)
  })

  const performanceSummaries = computed(() =>
    mergeHistoricalWithCurrent(seriesData.value ?? [], currentSummary.value)
  )

  const chartSummaries = computed<ChartSummary[]>(() => {
    const points = intradayData.value ?? []
    if (!isIntradayRange.value || points.length < MIN_INTRADAY_POINTS) {
      return performanceSummaries.value
    }
    return [...(seriesData.value?.slice(0, 1) ?? []), ...points]
  })

  const benchmarks = computed<ChartBenchmark[]>(() =>
    benchmarkQueries.map(({ benchmark, query }) => ({
      key: benchmark.key,
      label: benchmark.label,
      color: benchmark.color,
      points: query.data.value ?? [],
    }))
  )

  const sortedSummaries = computed(() => sortSummariesByDateAsc(summaries.value))

  const reversedSummaries = computed(() => [...sortedSummaries.value].reverse())

  const isLoading = computed(() => isLoadingHistorical.value || isLoadingCurrent.value)
  const error = computed(() => historicalError.value?.message || null)
  const rangeError = computed(
    () =>
      seriesError.value?.message ||
      rangeChangeError.value?.message ||
      intradayError.value?.message ||
      null
  )

  return {
    summaries,
    chartSummaries,
    performanceSummaries,
    benchmarks,
    rangeChange,
    ...calendar,
    sortedSummaries,
    reversedSummaries,
    isLoading,
    isFetching: isFetchingNextPage,
    isRangeLoading: computed(
      () => isFetchingSeries.value || (isIntradayRange.value && isIntradayStale.value)
    ),
    isRecalculating: recalculateMutation.isPending,
    error,
    rangeError,
    recalculationMessage,
    hasMoreData: hasNextPage,
    fetchSummaries: fetchNextPage,
    recalculate: () => recalculateMutation.mutate(),
  }
}
